import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getGetReconciliationQueryKey,
  getListAccountsQueryKey,
  getListLoansQueryKey,
  useCreateLoan,
  useDeleteLoan,
  useListCalendars,
  useListLoans,
  useReplaceLoan,
} from '../api/generated/fluence'
import type {
  AccountLedger,
  ConsumerLoan,
  ConsumerLoanInput,
  ScheduleFrequency,
} from '../api/generated/model'
import { formatDate, formatMoney, toMajor, toMinor } from '../lib/money'

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; id: string }

type EndKind = 'AFTER_OCCURRENCES' | 'ON_DATE'

type Draft = {
  id: string
  name: string
  accountId: string
  instalment: string
  currency: string
  firstPayment: string
  frequency: ScheduleFrequency
  endKind: EndKind
  endCount: string
  endDate: string
  convention: 'FOLLOWING' | 'PRECEDING' | 'MODIFIED_FOLLOWING' | 'UNADJUSTED'
  calendarId: string
  originalPrincipal: string
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function blank(accountId: string, currency: string, calendarId: string): Draft {
  return {
    id: '',
    name: '',
    accountId,
    instalment: '',
    currency,
    firstPayment: today(),
    frequency: 'MONTHLY',
    // A term in instalments is how loans are actually quoted — "60 months" — and it is the
    // form that survives moving the first payment date, which an end date does not.
    endKind: 'AFTER_OCCURRENCES',
    endCount: '60',
    endDate: '',
    convention: 'FOLLOWING',
    calendarId,
    originalPrincipal: '',
  }
}

function draftFrom(loan: ConsumerLoan): Draft {
  const schedule = loan.schedule
  return {
    id: loan.id,
    name: loan.name,
    accountId: loan.accountId,
    instalment: String(toMajor(loan.instalmentMinor, loan.currency)),
    currency: loan.currency,
    firstPayment: schedule.anchor ?? today(),
    frequency: schedule.frequency ?? 'MONTHLY',
    endKind: schedule.endKind === 'ON_DATE' ? 'ON_DATE' : 'AFTER_OCCURRENCES',
    endCount: String(schedule.endCount ?? 60),
    endDate: schedule.endDate ?? '',
    convention: loan.convention,
    calendarId: loan.calendarId,
    originalPrincipal: loan.originalPrincipalMinor
      ? String(toMajor(loan.originalPrincipalMinor, loan.currency))
      : '',
  }
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
}

/**
 * Managing consumer loans.
 *
 * The list leads with the payoff date and how many instalments remain, because that is the
 * question a loan raises that a recurring outflow does not: not "what leaves each month"
 * but "when am I free of this". Both come from the server already resolved — recomputing
 * them here from anchor, frequency and term would be a second implementation of the
 * schedule.
 */
export function LoanEditor({ accounts }: { accounts: AccountLedger[] }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>({ kind: 'idle' })
  const [draft, setDraft] = useState<Draft>(blank('', 'USD', 'US'))
  const [error, setError] = useState<string | null>(null)
  const [idTouched, setIdTouched] = useState(false)

  const loans = useListLoans()
  const calendars = useListCalendars()

  const refresh = async () => {
    await Promise.all(
      [
        getListLoansQueryKey(),
        getListAccountsQueryKey(),
        getGetReconciliationQueryKey(),
        getGetProjectionQueryKey().slice(0, 1),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }
  const create = useCreateLoan({ mutation: { onSuccess: refresh } })
  const replace = useReplaceLoan({ mutation: { onSuccess: refresh } })
  const remove = useDeleteLoan({ mutation: { onSuccess: refresh } })

  const list: ConsumerLoan[] = loans.data?.status === 200 ? loans.data.data : []
  const calendarList = calendars.data?.status === 200 ? calendars.data.data : []
  const busy = create.isPending || replace.isPending || remove.isPending
  const isNew = mode.kind === 'new'

  function startNew() {
    setError(null)
    setIdTouched(false)
    const account = accounts[0]
    setDraft(
      blank(account?.accountId ?? '', account?.currency ?? 'USD', calendarList[0]?.id ?? 'US'),
    )
    setMode({ kind: 'new' })
  }

  function build(): ConsumerLoanInput | string {
    if (!draft.name.trim()) return 'Give it a name.'
    if (!draft.id.trim()) return 'An id is required.'
    if (!draft.accountId) return 'Choose the account it is repaid from.'
    const instalmentMinor = toMinor(draft.instalment, draft.currency)
    if (instalmentMinor === null || instalmentMinor < 0) {
      return 'The instalment must be a non-negative number.'
    }
    const principal = draft.originalPrincipal.trim()
      ? toMinor(draft.originalPrincipal, draft.currency)
      : null
    if (draft.originalPrincipal.trim() && principal === null) {
      return 'The amount borrowed must be a number.'
    }
    // Refused here as well as by the server: a loan that never finishes would subtract
    // from every projection for ever, and saying so before the round trip is kinder.
    if (draft.endKind === 'AFTER_OCCURRENCES' && !(Number(draft.endCount) > 0)) {
      return 'A loan has to end. Give the number of instalments.'
    }
    if (draft.endKind === 'ON_DATE' && !draft.endDate) {
      return 'A loan has to end. Give the date of the last payment.'
    }

    return {
      id: draft.id.trim(),
      name: draft.name.trim(),
      accountId: draft.accountId,
      instalmentMinor,
      currency: draft.currency,
      schedule: {
        kind: 'RECURRING',
        anchor: draft.firstPayment,
        frequency: draft.frequency,
        ...(draft.endKind === 'AFTER_OCCURRENCES'
          ? { endKind: 'AFTER_OCCURRENCES' as const, endCount: Number(draft.endCount) }
          : { endKind: 'ON_DATE' as const, endDate: draft.endDate }),
      },
      convention: draft.convention,
      calendarId: draft.calendarId,
      originalPrincipalMinor: principal ?? undefined,
    }
  }

  async function submit() {
    const built = build()
    if (typeof built === 'string') {
      setError(built)
      return
    }
    setError(null)
    const response =
      mode.kind === 'edit'
        ? await replace.mutateAsync({ id: mode.id, data: built })
        : await create.mutateAsync({ data: built })
    if (response.status === 200 || response.status === 201) {
      setMode({ kind: 'idle' })
      return
    }
    setError(detail(response) ?? 'The server rejected that.')
  }

  async function confirmDelete(loan: ConsumerLoan) {
    if (!window.confirm(`Delete "${loan.name}"? This cannot be undone.`)) return
    const response = await remove.mutateAsync({ id: loan.id })
    if (response.status !== 204) setError(detail(response) ?? 'That loan could not be deleted.')
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Loans</h2>
        {mode.kind === 'idle' && (
          <button type="button" className="btn-primary btn-small" onClick={startNew}>
            Add
          </button>
        )}
      </div>

      {error && <p className="cf-error">{error}</p>}

      {mode.kind !== 'idle' && (
        <form
          className="cf-form"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div className="cf-grid">
            <label>
              <span>Name</span>
              <input
                value={draft.name}
                autoFocus
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    name: e.target.value,
                    id: isNew && !idTouched ? slugify(e.target.value) : draft.id,
                  })
                }
                placeholder="Car loan"
              />
            </label>
            <label>
              <span>Id</span>
              <input
                value={draft.id}
                disabled={!isNew}
                onChange={(e) => {
                  setIdTouched(true)
                  setDraft({ ...draft, id: e.target.value })
                }}
              />
            </label>
            <label>
              <span>Repaid from</span>
              <select
                value={draft.accountId}
                onChange={(e) => {
                  const next = accounts.find((a) => a.accountId === e.target.value)
                  setDraft({
                    ...draft,
                    accountId: e.target.value,
                    currency: next?.currency ?? draft.currency,
                  })
                }}
              >
                {accounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Instalment ({draft.currency})</span>
              <input
                value={draft.instalment}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, instalment: e.target.value })}
                placeholder="415.00"
              />
            </label>
            <label>
              <span>First payment</span>
              <input
                type="date"
                value={draft.firstPayment}
                onChange={(e) => setDraft({ ...draft, firstPayment: e.target.value })}
              />
            </label>
            <label>
              <span>Every</span>
              <select
                value={draft.frequency}
                onChange={(e) =>
                  setDraft({ ...draft, frequency: e.target.value as ScheduleFrequency })
                }
              >
                <option value="MONTHLY">Month</option>
                <option value="BIWEEKLY">Two weeks</option>
                <option value="WEEKLY">Week</option>
                <option value="QUARTERLY">Quarter</option>
                <option value="SEMIANNUAL">Half year</option>
                <option value="ANNUAL">Year</option>
              </select>
            </label>
            <label>
              <span>Term</span>
              <select
                value={draft.endKind}
                onChange={(e) => setDraft({ ...draft, endKind: e.target.value as EndKind })}
              >
                <option value="AFTER_OCCURRENCES">A number of instalments</option>
                <option value="ON_DATE">Until a date</option>
              </select>
            </label>
            {draft.endKind === 'AFTER_OCCURRENCES' ? (
              <label>
                <span>Instalments</span>
                <input
                  value={draft.endCount}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, endCount: e.target.value })}
                />
              </label>
            ) : (
              <label>
                <span>Last payment</span>
                <input
                  type="date"
                  value={draft.endDate}
                  onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                />
              </label>
            )}
            <label>
              <span>Calendar</span>
              <select
                value={draft.calendarId}
                onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}
              >
                {calendarList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>If it lands on a closed day</span>
              <select
                value={draft.convention}
                onChange={(e) =>
                  setDraft({ ...draft, convention: e.target.value as Draft['convention'] })
                }
              >
                <option value="FOLLOWING">Move to the next business day</option>
                <option value="MODIFIED_FOLLOWING">Next business day, but stay in the month</option>
                <option value="PRECEDING">Move to the previous business day</option>
                <option value="UNADJUSTED">Leave it</option>
              </select>
            </label>
            <label>
              <span>Amount borrowed ({draft.currency}, optional)</span>
              <input
                value={draft.originalPrincipal}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, originalPrincipal: e.target.value })}
                placeholder="18500.00"
              />
            </label>
          </div>

          <div className="cf-actions">
            <button type="button" className="btn-ghost" onClick={() => setMode({ kind: 'idle' })}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : isNew ? 'Add loan' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      <ul className="cf-list">
        {list.map((loan) => (
          <li key={loan.id}>
            <div className="cf-main">
              <span className="cf-name">{loan.name}</span>
              <span className="cf-when">
                {`from ${accounts.find((a) => a.accountId === loan.accountId)?.name ?? loan.accountId}`}
                {` · ${loan.remainingInstalments} left, paid off ${formatDate(loan.payoffDate)}`}
              </span>
            </div>
            <span className="cf-amount is-out">−{formatMoney(loan.instalmentMinor, loan.currency)}</span>
            <div className="cf-buttons">
              <button
                type="button"
                className="btn-ghost btn-small"
                onClick={() => {
                  setError(null)
                  setIdTouched(true)
                  setDraft(draftFrom(loan))
                  setMode({ kind: 'edit', id: loan.id })
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-small btn-danger"
                onClick={() => void confirmDelete(loan)}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function detail(response: unknown): string | null {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data: unknown }).data
    if (data && typeof data === 'object' && 'detail' in data) {
      const value = (data as { detail: unknown }).detail
      if (typeof value === 'string') return value
    }
  }
  return null
}
